<template>
    <div>

        <b-card no-body>
            <b-card-header>
                <h5 class="mb-0 text-white"><b>GENERAR FORMULARIOS DE PRUEBA</b></h5>
            </b-card-header>
            <b-card-body class="pt-2">

                <b-alert show variant="warning" class="p-1 mb-2">
                    <strong>NOTA:</strong> Esta herramienta genera formularios de prueba para que los proveedores puedan completar el flujo de CTS o Gratificación.
                    Los formularios se crean con un deadline de 7 días.
                </b-alert>

                <b-row>

                    <b-col cols="12" md="6" class="mb-2">
                        <label><strong>TIPO DE FORMULARIO</strong></label>
                        <v-select
                            v-model="selectedType"
                            :options="typeOptions"
                            :clearable="false"
                            class="w-100"
                        />
                    </b-col>

                    <b-col cols="12" md="6" class="mb-2">
                        <label><strong>PROVEEDORES (opcional, vacío = todos)</strong></label>
                        <v-select
                            v-model="selectedProviders"
                            :options="providerOptions"
                            class="w-100"
                            multiple
                            :loading="loadingProviders"
                        />
                    </b-col>

                </b-row>

                <b-button
                    variant="primary"
                    block
                    :disabled="processing"
                    @click="generateForms"
                >
                    <b-spinner v-if="processing" small class="mr-1" />
                    {{ processing ? 'GENERANDO...' : 'GENERAR FORMULARIOS' }}
                </b-button>

            </b-card-body>
        </b-card>

        <!-- Results -->
        <b-card v-if="result" no-body>
            <b-card-header>
                <h5 class="mb-0 text-white"><b>RESULTADO</b></h5>
            </b-card-header>
            <b-card-body class="pt-2">

                <b-alert show :variant="result.success ? 'success' : 'danger'" class="p-1 mb-2">
                    <strong>{{ result.message }}</strong>
                </b-alert>

                <div v-if="result.start_date" class="mb-1">
                    <strong>Fecha inicio (start_date):</strong> {{ result.start_date }}
                </div>
                <div v-if="result.deadline" class="mb-2">
                    <strong>Deadline:</strong> {{ formatDeadline(result.deadline) }}
                </div>

                <b-table
                    v-if="result.forms && result.forms.length"
                    striped
                    hover
                    responsive
                    class="text-center mb-0"
                    :items="result.forms"
                    :fields="resultFields"
                    show-empty
                    empty-text="Sin resultados"
                >
                    <template #cell(status)="data">
                        <b-badge :variant="data.item.status === 'created' ? 'light-success' : 'light-warning'">
                            {{ data.item.status === 'created' ? 'CREADO' : 'YA EXISTÍA' }}
                        </b-badge>
                    </template>
                </b-table>

            </b-card-body>
        </b-card>

        <!-- Test Emails Section -->
        <b-card no-body>
            <b-card-header>
                <h5 class="mb-0 text-white"><b>ENVIAR CORREOS DE PRUEBA</b></h5>
            </b-card-header>
            <b-card-body class="pt-2">

                <b-alert show variant="info" class="p-1 mb-2">
                    <strong>INFO:</strong> Envía un ejemplo de cada plantilla de correo modificada para verificar su contenido.
                    Se enviarán 5 correos: Formulario Asignado (MENSUAL, CTS, GRATIFICACIÓN) + Aviso Previo (CTS, Gratificación).
                </b-alert>

                <b-row>
                    <b-col cols="12" md="6" class="mb-2">
                        <label><strong>CORREO DE DESTINO</strong></label>
                        <b-form-input
                            v-model="testEmail"
                            type="email"
                            placeholder="correo@ejemplo.com"
                        />
                    </b-col>
                </b-row>

                <b-button
                    variant="warning"
                    block
                    :disabled="sendingEmails || !testEmail"
                    @click="sendTestEmails"
                >
                    <b-spinner v-if="sendingEmails" small class="mr-1" />
                    {{ sendingEmails ? 'ENVIANDO...' : 'ENVIAR CORREOS DE PRUEBA' }}
                </b-button>

            </b-card-body>
        </b-card>

        <!-- Email Results -->
        <b-card v-if="emailResult" no-body>
            <b-card-header>
                <h5 class="mb-0 text-white"><b>RESULTADO DE CORREOS</b></h5>
            </b-card-header>
            <b-card-body class="pt-2">

                <b-alert show :variant="emailResult.success ? 'success' : 'danger'" class="p-1 mb-2">
                    <strong>{{ emailResult.message }}</strong>
                </b-alert>

                <b-table
                    v-if="emailResult.results && emailResult.results.length"
                    striped
                    hover
                    responsive
                    class="text-center mb-0"
                    :items="emailResult.results"
                    :fields="emailResultFields"
                    show-empty
                    empty-text="Sin resultados"
                >
                    <template #cell(status)="data">
                        <b-badge :variant="data.item.status === 'sent' ? 'light-success' : 'light-danger'">
                            {{ data.item.status === 'sent' ? 'ENVIADO' : 'ERROR' }}
                        </b-badge>
                    </template>
                </b-table>

            </b-card-body>
        </b-card>

    </div>
</template>

<script>

    import { ref, onBeforeMount } from '@vue/composition-api';
    import vSelect from 'vue-select';
    import moment from 'moment';
    import axios from '@axios';

    export default {
        components: {
            vSelect
        },
        setup() {

            const processing = ref(false);
            const loadingProviders = ref(false);
            const result = ref(null);

            const typeOptions = ref([
                { label: 'CTS', value: 'cts' },
                { label: 'GRATIFICACIÓN', value: 'gratification' }
            ]);

            const selectedType = ref({ label: 'CTS', value: 'cts' });
            const selectedProviders = ref([]);
            const providerOptions = ref([]);

            const resultFields = ref([
                { key: 'provider', label: 'PROVEEDOR', sortable: false },
                { key: 'form_id', label: 'FORM ID', sortable: false },
                { key: 'status', label: 'ESTADO', sortable: false }
            ]);

            // Email test
            const testEmail = ref('');
            const sendingEmails = ref(false);
            const emailResult = ref(null);

            const emailResultFields = ref([
                { key: 'template', label: 'PLANTILLA', sortable: false },
                { key: 'status', label: 'ESTADO', sortable: false }
            ]);

            onBeforeMount(async () => {
                await loadProviders();
            });

            const loadProviders = async () => {
                loadingProviders.value = true;
                try {
                    const { data } = await axios.get('/reports/cts-cartas/filters');
                    providerOptions.value = data.data.providers || [];
                } catch (e) {
                    providerOptions.value = [];
                }
                loadingProviders.value = false;
            };

            const generateForms = async () => {
                processing.value = true;
                result.value = null;

                try {
                    const payload = {
                        type: selectedType.value.value,
                        provider_ids: selectedProviders.value.map(p => p.value)
                    };

                    const { data } = await axios.post('/evidence/generate-test-forms', payload);
                    result.value = data;
                } catch (err) {
                    result.value = {
                        success: false,
                        message: err.response?.data?.message || 'Error al generar formularios.'
                    };
                }

                processing.value = false;
            };

            const sendTestEmails = async () => {
                sendingEmails.value = true;
                emailResult.value = null;

                try {
                    const { data } = await axios.post('/evidence/send-test-emails', {
                        email: testEmail.value
                    });
                    emailResult.value = data;
                } catch (err) {
                    emailResult.value = {
                        success: false,
                        message: err.response?.data?.message || 'Error al enviar correos.'
                    };
                }

                sendingEmails.value = false;
            };

            const formatDeadline = (deadline) => {
                return moment(deadline).format('DD/MM/YYYY HH:mm');
            };

            return {
                processing,
                loadingProviders,
                result,
                typeOptions,
                selectedType,
                selectedProviders,
                providerOptions,
                resultFields,
                generateForms,
                formatDeadline,

                // Email test
                testEmail,
                sendingEmails,
                emailResult,
                emailResultFields,
                sendTestEmails
            };
        }
    }

</script>

<style lang="scss" scoped>

    .card-header {
        background-color: var(--primary);
        padding: 1rem 1.5rem;
    }

</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
